export type SystemMessage = {
  id: string
  text: string
  type: 'info' | 'roast' | 'loot' | 'system' | 'achievement' | 'mordecai'
}

export type DialogueLine = {
  speaker:
    | 'system'
    | 'mordecai'
    | 'donut'
    | 'gnome'
    | 'goblin_king'
    | 'narrator'
    | 'carl'
  text: string
  portrait?: string
  duration?: number // ms, default 4000
}

export type InventoryItem = {
  id: string
  name: string
  description: string
  type: 'consumable' | 'weapon' | 'oddity' | 'quest'
  effect?: 'heal' | 'buff' | 'throw'
  value?: number
  quantity?: number
}

export type ZoneId =
  | 'awakening'
  | 'first_breath'
  | 'tutorial_hollow'
  | 'donut_discovery'
  | 'merchant_nook'
  | 'goblin_warrens'
  | 'throne_of_bones'
  | 'stairs_down'

export type GameState = {
  // Vitals
  health: number
  maxHealth: number
  viewership: number
  peakViewership: number
  kills: number
  cratesDestroyed: number
  timeAlive: number
  gold: number

  // Meta
  isGameOver: boolean
  deathCause: string
  isBarefoot: boolean
  floor: number

  // Flow state: 'start' -> 'cold_open' -> 'playing' -> 'floor_complete' -> 'playing' (next floor)
  phase: 'start' | 'cold_open' | 'playing' | 'floor_complete'

  // World/zone
  currentZone: ZoneId
  visitedZones: ZoneId[]
  storyFlags: Record<string, boolean>
  zoneBanner: { title: string; subtitle: string; key: number } | null

  // Dialogue
  dialogueQueue: DialogueLine[]

  // Inventory
  inventory: InventoryItem[]

  // UI
  systemMessages: SystemMessage[]
  shopOpen: boolean
}

const initialState: GameState = {
  health: 100,
  maxHealth: 100,
  viewership: 10,
  peakViewership: 10,
  kills: 0,
  cratesDestroyed: 0,
  timeAlive: 0,
  gold: 15,
  isGameOver: false,
  deathCause: '',
  isBarefoot: true,
  floor: 1,
  phase: 'start',
  currentZone: 'awakening',
  visitedZones: [],
  storyFlags: {},
  zoneBanner: null,
  dialogueQueue: [],
  inventory: [],
  systemMessages: [],
  shopOpen: false,
}

class GameStore {
  private state: GameState = { ...initialState, storyFlags: {} }
  private listeners: Set<(state: GameState) => void> = new Set()

  getState() {
    return this.state
  }

  setState(newState: Partial<GameState>) {
    this.state = { ...this.state, ...newState }
    if (this.state.viewership > this.state.peakViewership) {
      this.state.peakViewership = this.state.viewership
    }
    this.listeners.forEach((listener) => listener(this.state))
  }

  subscribe(listener: (state: GameState) => void) {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  addMessage(text: string, type: SystemMessage['type'] = 'system') {
    const id = Math.random().toString(36).substring(2, 9)
    this.setState({
      systemMessages: [...this.state.systemMessages, { id, text, type }],
    })
    const duration = type === 'achievement' ? 4000 : 3500
    setTimeout(() => {
      this.setState({
        systemMessages: this.state.systemMessages.filter((m) => m.id !== id),
      })
    }, duration)
  }

  queueDialogue(lines: DialogueLine[]) {
    this.setState({ dialogueQueue: [...this.state.dialogueQueue, ...lines] })
  }

  advanceDialogue() {
    this.setState({ dialogueQueue: this.state.dialogueQueue.slice(1) })
  }

  showZoneBanner(title: string, subtitle: string) {
    this.setState({ zoneBanner: { title, subtitle, key: Date.now() } })
    setTimeout(() => {
      if (this.state.zoneBanner?.title === title) {
        this.setState({ zoneBanner: null })
      }
    }, 3500)
  }

  setStoryFlag(flag: string) {
    this.setState({ storyFlags: { ...this.state.storyFlags, [flag]: true } })
  }

  hasStoryFlag(flag: string): boolean {
    return !!this.state.storyFlags[flag]
  }

  addItem(item: InventoryItem) {
    const existing = this.state.inventory.find((i) => i.id === item.id)
    if (existing) {
      const updated = this.state.inventory.map((i) =>
        i.id === item.id
          ? { ...i, quantity: (i.quantity || 1) + (item.quantity || 1) }
          : i,
      )
      this.setState({ inventory: updated })
    } else {
      this.setState({
        inventory: [
          ...this.state.inventory,
          { ...item, quantity: item.quantity || 1 },
        ],
      })
    }
  }

  removeItem(id: string) {
    const item = this.state.inventory.find((i) => i.id === id)
    if (!item) return
    if ((item.quantity || 1) > 1) {
      this.setState({
        inventory: this.state.inventory.map((i) =>
          i.id === id ? { ...i, quantity: (i.quantity || 1) - 1 } : i,
        ),
      })
    } else {
      this.setState({
        inventory: this.state.inventory.filter((i) => i.id !== id),
      })
    }
  }

  useItem(id: string) {
    const item = this.state.inventory.find((i) => i.id === id)
    if (!item) return
    if (item.effect === 'heal' && item.value) {
      this.setState({
        health: Math.min(this.state.maxHealth, this.state.health + item.value),
      })
      this.addMessage(`Consumed ${item.name}. Tastes like pennies.`, 'loot')
    }
    this.removeItem(id)
  }

  resetGame() {
    this.setState({
      ...initialState,
      phase: 'playing',
      currentZone: 'awakening',
      visitedZones: [],
      storyFlags: {},
      systemMessages: [],
      dialogueQueue: [],
      inventory: [],
    })
  }
}

export const gameStore = new GameStore()
